<template>
  <el-dialog
    title="选择费用组"
    :visible.sync="dialogVisible"
    width="900px"
    @close="handleClose"
    :append-to-body="true"
  >
    <div class="fee-group-container">
      <!-- 港口信息 -->
      <div class="port-info">
        <div class="info-item">
          <span class="label">起运港:</span>
          <span class="value">{{ portInfo.polName }}</span>
        </div>
        <div class="info-item">
          <span class="label">目的港:</span>
          <span class="value">{{ portInfo.podName }}</span>
        </div>
      </div>
      
      <!-- 费用组选择 -->
      <div class="fee-groups">
        <h3>请选择需要的费用组</h3>
        <div class="checkbox-group">
          <el-checkbox v-model="selectedGroups.ocean" label="ocean">海运费</el-checkbox>
          <el-checkbox v-model="selectedGroups.pol" label="pol">起运港费用</el-checkbox>
          <el-checkbox v-if="!isContainer" v-model="selectedGroups.warehouse" label="warehouse">仓库费用</el-checkbox>
          <el-checkbox v-if="!isContainer" v-model="selectedGroups.pod" label="pod">目的港费用</el-checkbox>
        </div>
      </div>
    </div>
    
    <!-- 底部按钮 -->
    <div slot="footer" class="dialog-footer">
      <el-button @click="handleCancel">取 消</el-button>
      <el-button type="primary" @click="handleSave">保 存</el-button>
    </div>
  </el-dialog>
</template>

<script>
export default {
  name: "FeeGroupSelect",
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    quotation: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      dialogVisible: false,
      selectedGroups: {
        ocean: true,    // 默认选中海运费
        pol: false,
        warehouse: false,
        pod: false
      },
      selectedFees: [],     // 已选择的费用
    };
  },
  computed: {
    // 是否为集装箱运输
    isContainer() {
      return this.quotation && this.quotation.spell_type === 1;
    },
    
    // 根据运输类型显示不同的港口名称
    portInfo() {
      if (this.isContainer) {
        // 整箱运输
        return {
          polName: this.quotation ? (this.quotation.from_port_name_en || '') : '',
          podName: this.quotation ? (this.quotation.dest_port_name_en || '') : ''
        };
      } else {
        // 拼箱运输
        return {
          polName: this.quotation ? (this.quotation.from_port || '') : '',
          podName: this.quotation ? (this.quotation.dest_port || '') : ''
        };
      }
    }
  },
  watch: {
    visible(val) {
      this.dialogVisible = val;
      // 当窗口打开时重置selectedFees
      if (val) {
        this.selectedFees = [];
      }
    },
    dialogVisible(val) {
      this.$emit('update:visible', val);
      // 当窗口关闭时清空selectedFees
      if (!val) {
        this.selectedFees = [];
      }
    },
    // 当quotation变化时，重置warehouse和pod选择状态
    quotation: {
      handler(val) {
        if (val && val.spell_type === 1) {
          // 如果是集装箱运输，重置这两个选项为false
          this.selectedGroups.warehouse = false;
          this.selectedGroups.pod = false;
        }
      },
      immediate: true
    }
  },
  methods: {
    handleCancel() {
      // 取消时清空selectedFees
      this.selectedFees = [];
      this.$emit('cancel');
      this.dialogVisible = false;
    },
    handleClose() {
      // 关闭时清空selectedFees
      this.selectedFees = [];
      this.$emit('cancel');
      this.dialogVisible = false;
    },
    handleSave() {
      const selectedFeeGroups = Object.keys(this.selectedGroups).filter(key => this.selectedGroups[key]);
      
      // 如果没有选择任何费用组，提示用户
      if (selectedFeeGroups.length === 0) {
        this.$message.warning('请至少选择一个费用组');
        return;
      }

      // 保存前清空已选择的费用
      this.selectedFees = [];

      // 集装箱运输情况下的费用处理
      const promises = [];
      
      if (this.isContainer) {
        // 1. 处理海运费
        if (this.selectedGroups.ocean) {
          const freight_params = {
            freight_id: this.quotation.id,
            user_type: '2',
            dangerous_status: '0'
          };
          
          const oceanPromise = this.$store.dispatch("API_bookingSpace/fcl_freight_search", freight_params)
            .then(res => {
              if (res && res.data) {
                const data = res.data;
                // 添加海运费到已选费用
                this.selectedFees.push({
                  cost_name: '海运费',
                  oversea: '1',
                  measure_unit: '5',
                  currency: data.currency_code,
                  quantity: '1',
                  price: data.price_20gp,
                  unit_price: data.price_20gp,
                  measure_name_zh: '20GP',
                  measure_code: 'CTN'
                });
              }
            })
            
          promises.push(oceanPromise);
        }
        // 2. 处理起运港费用
        if (this.selectedGroups.pol) {
          const pol_params = {
            freight_id: this.quotation.id,
            cargo_type: '2',
            spell_type: '1',
            eq_types: '20GP'
          };
          
          const polPromise = this.$store.dispatch("API_bookingSpace/fcl_fee_search", pol_params)
            .then(res => {
              if (res && res.data[0].data_list) {
                // 遍历起运港费用数据
                res.data[0].data_list.forEach(item => {
                  this.selectedFees.push({
                    cost_name: item.name_zh,
                    oversea: '1',
                    measure_unit: item.measure_id,
                    currency: item.code,
                    quantity: item.per_plan_fee_price_num,
                    price: item.GP20 || item.price,
                    unit_price: item.GP20 || item.price,
                    measure_name_zh: item.measure_name_zh,
                    measure_code: item.measure_code
                  });
                });
              }
            })
            .catch(err => {
              console.error('获取起运港费用失败:', err);
            });
            
          promises.push(polPromise);
        }
      } else {
        // 1. 处理海运费
        if (this.selectedGroups.ocean) {
          const freight_params = {
            freight_id: this.quotation.id,
            booking_space: '1'
          };
          
          const oceanPromise = this.$store.dispatch("API_bookingSpace/lcl_freight_search", freight_params)
            .then(res => {
              if (res && res.data) {
                const data = res.data;
                // 添加海运费到已选费用
                this.selectedFees.push({
                  cost_name: '海运费',
                  oversea: '1',
                  measure_unit: '3',
                  currency: data.code,
                  quantity: '2',
                  unit_price: data.price_lcl_dangerous,
                  price: data.price_lcl_dangerous * 2,
                  measure_name_zh: '体积价',
                  measure_code: data.measure_unit
                });
              }
            })
          promises.push(oceanPromise);
        }

        // 2. 处理起运港费用
        if (this.selectedGroups.pol || this.selectedGroups.warehouse || this.selectedGroups.pod) {
          const pol_params = {
            freight_id: this.quotation.id,
            cargo_type: '1',
            spell_type: '2',
            volume: '2',
            weight: '1000',
            piece_num: '1',
            measurement_standard: '2'
          };
          
          const polPromise = this.$store.dispatch("API_bookingSpace/fcl_fee_search", pol_params)
            .then(res => {
              if (this.selectedGroups.pol && res.data[0].data_list) {
                // 遍历起运港费用数据
                res.data[0].data_list.forEach(item => {
                  this.selectedFees.push({
                    cost_name: item.name_zh,
                    oversea: '1',
                    measure_unit: item.measure_id,
                    currency: item.code,
                    quantity: item.per_plan_fee_price_num,
                    unit_price: item.GP20 || item.price,
                    price: item.total_price,
                    measure_name_zh: item.measure_name_zh,
                    measure_code: item.measure_code
                  });
                });
              }

              if (this.selectedGroups.warehouse && res.data[1].data_list) {
                // 遍历仓库费用数据
                res.data[1].data_list.forEach(item => {
                  this.selectedFees.push({
                    cost_name: item.name_zh,
                    oversea: '1',
                    measure_unit: item.measure_id,
                    currency: item.code,
                    quantity: item.per_plan_fee_price_num,
                    unit_price: item.GP20 || item.price,
                    price: item.total_price,
                    measure_name_zh: item.measure_name_zh,
                    measure_code: item.measure_code
                  });
                });
              }

              if (this.selectedGroups.pod && res.data[2].data_list) {
                // 遍历目的港费用数据
                res.data[2].data_list.forEach(item => {
                  this.selectedFees.push({
                    cost_name: item.name_zh,
                    oversea: '2',
                    measure_unit: item.measure_id,
                    currency: item.code,
                    quantity: item.per_plan_fee_price_num,
                    unit_price: item.GP20 || item.price,
                    price: item.total_price,
                    measure_name_zh: item.measure_name_zh,
                    measure_code: item.measure_code
                  });
                });
              }
            })
            .catch(err => {
              console.error('获取起运港费用失败:', err);
            });
            
          promises.push(polPromise);
        }

      }

      // 等待所有费用获取完成后，发送数据到父组件
      Promise.all(promises)
        .then(() => {
          // 发送数据给父组件
          this.$emit('confirm', this.selectedFees);
          
          // 关闭当前对话框
          this.dialogVisible = false;
          
          // 添加成功提示
          if (this.selectedFees.length > 0) {
            this.$message.success(`成功添加 ${this.selectedFees.length} 项费用`);
            
            // 发送完数据后清空selectedFees
            setTimeout(() => {
              this.selectedFees = [];
            }, 0);
          } else {
            this.$message.warning('未获取到任何费用');
          }
        })
        .catch(err => {
          // 发生错误时也清空selectedFees
          this.selectedFees = [];
          console.error('获取费用失败:', err);
          this.$message.error('获取费用失败，请重试');
        });
    },
    
    // 查找 OrderQuotationAdd 父组件
    findOrderQuotationAddComponent() {
      let parent = this.$parent;
      
      while (parent) {
        if (parent.$options && parent.$options.name === 'OrderQuotationAdd') {
          return parent;
        }
        parent = parent.$parent;
      }
      
      return null;
    }
  }
};
</script>

<style scoped lang="less">
.fee-group-container {
  padding: 0 20px;
  
  .port-info {
    margin-bottom: 30px;
    padding: 15px;
    background-color: #f5f7fa;
    border-radius: 4px;
    
    .info-item {
      margin-bottom: 10px;
      font-size: 14px;
      
      &:last-child {
        margin-bottom: 0;
      }
      
      .label {
        display: inline-block;
        width: 70px;
        font-weight: bold;
        color: #606266;
      }
      
      .value {
        color: #303133;
      }
    }
  }
  
  .fee-groups {
    h3 {
      margin-top: 0;
      margin-bottom: 20px;
      font-size: 16px;
      font-weight: 500;
      color: #303133;
    }
    
    .checkbox-group {
      display: flex;
      flex-wrap: wrap;
      
      .el-checkbox {
        margin-right: 30px;
        margin-bottom: 15px;
        
        &:last-child {
          margin-right: 0;
        }
      }
    }
  }
}

.dialog-footer {
  text-align: right;
}
</style>
